import { isUserVisibleRenderType } from '@/types/render';
import { RenderStatus } from '@/shared/constants';
import { isRenderInQueue } from '@/shared/lib/render-status';
import {
  isUpscaleJob,
  type Job,
  type RenderJob,
  type UpscaleJob,
  type AlbumJob,
} from '../job-queue.types';
import type { JobQueueStateCreator, SelectorsSlice } from './types';

export const createSelectorsSlice: JobQueueStateCreator<SelectorsSlice> = (
  _set,
  get
) => ({
  getProcessingJobs: () => {
    const state = get();
    return state.jobQueue.filter((r) => isRenderInQueue(r.status));
  },

  getJobById: (jobId) => get().jobQueue.find((j) => j.id === jobId),

  getJobForConceptAngle: (conceptAngleId) =>
    get().jobQueue.find(
      (j) => j.conceptAngleId === conceptAngleId && isRenderInQueue(j.status)
    ),

  getJobForConcept: (conceptId) =>
    get().jobQueue.find(
      (j) => j.conceptId === conceptId && isRenderInQueue(j.status)
    ),

  getCompletedJobForConceptAngle: (conceptAngleId: string): Job | undefined => {
    const state = get();

    // Backend removes completed jobs from the queue quickly; the matching
    // Render entity itself lives in TanStack Query (STATE_POLICY) and is
    // consumed via `useRendersForConcept` / `useAngleRendersData`. This
    // selector only surfaces the in-queue Job for very recent completions
    // that the UI needs to render the success overlay against.
    const completedJobsInQueue = state.jobQueue.filter(
      (j): j is RenderJob | UpscaleJob =>
        (j.jobType === 'render' || j.jobType === 'upscale') &&
        j.conceptAngleId === conceptAngleId &&
        j.status === RenderStatus.COMPLETED &&
        j.imageUrl !== null &&
        (j.jobType === 'upscale' || isUserVisibleRenderType(j.scenarioType))
    );
    return completedJobsInQueue[completedJobsInQueue.length - 1];
  },

  getProcessingJobCount: () =>
    get().jobQueue.filter((r) => isRenderInQueue(r.status)).length,

  // Counts only render-quota jobs (renders + upscales), excludes albums and
  // estimates. Mirrors server-side RenderLimitsService::enforceQueueLimit
  // which gates `limit_generations_queue` (parallel render slots). Albums and
  // estimates use their own limits and must not consume render slots — keeps
  // WEB aligned with Apple/Android.
  getProcessingRenderJobCount: () =>
    get().jobQueue.filter(
      (r) =>
        r.jobType !== 'album' &&
        r.jobType !== 'estimate' &&
        isRenderInQueue(r.status)
    ).length,

  getRecentlyCompletedAlbums: (conceptId: string): AlbumJob[] =>
    get().jobQueue.filter(
      (item): item is AlbumJob =>
        item.jobType === 'album' &&
        item.conceptId === conceptId &&
        item.status === RenderStatus.COMPLETED
    ),

  // Get completed jobs (renders or upscales) for multiple angles
  // Returns Map for O(1) lookup performance
  // Returns last (newest) completed job for each angle
  // Checks both jobQueue and renders Map (backend cleans queue quickly)
  // Note: Zustand handles shallow equality - consumers should use useMemo if needed
  getCompletedJobsForAngles: (angleIds: string[]): Map<string, Job> => {
    const state = get();
    const resultMap = new Map<string, Job>();

    // Early return for empty input
    if (angleIds.length === 0) {
      return resultMap;
    }

    // Process each angle
    angleIds.forEach((angleId) => {
      // Use the existing getCompletedJobForConceptAngle method
      // which handles both jobQueue and renders Map fallback
      const job = state.getCompletedJobForConceptAngle(angleId);
      if (job) {
        resultMap.set(angleId, job);
      }
    });

    return resultMap;
  },

  // Get active upscale for specific render
  getActiveUpscaleForRender: (renderId: string): UpscaleJob | undefined => {
    const state = get();
    const job = state.jobQueue.find(
      (j) => j.jobType === 'upscale' && isRenderInQueue(j.status)
    );

    // Type guard: if jobType === 'upscale', TypeScript knows it's UpscaleJob
    if (job && isUpscaleJob(job) && job.originalRenderId === renderId) {
      return job;
    }
    return undefined;
  },

  // Get active upscales for multiple renders
  // Returns Map for O(1) lookup performance
  // Note: Zustand handles shallow equality - consumers should use useMemo if needed
  getActiveUpscalesForRenders: (
    renderIds: string[]
  ): Map<string, UpscaleJob> => {
    const state = get();
    const resultMap = new Map<string, UpscaleJob>();

    // Early return for empty input
    if (renderIds.length === 0) {
      return resultMap;
    }

    // Single pass through renderIds - find active upscale for each
    renderIds.forEach((renderId) => {
      const upscale = state.jobQueue.find(
        (j): j is UpscaleJob =>
          j.jobType === 'upscale' &&
          j.originalRenderId === renderId &&
          isRenderInQueue(j.status)
      );

      if (upscale) {
        resultMap.set(renderId, upscale);
      }
    });

    return resultMap;
  },

  getActiveAlbumForConcept: (conceptId: string): AlbumJob | undefined =>
    get().jobQueue.find(
      (j): j is AlbumJob =>
        j.jobType === 'album' &&
        j.conceptId === conceptId &&
        isRenderInQueue(j.status)
    ),

  getCompletedAlbumsForConcept: (conceptId: string): AlbumJob[] =>
    get().jobQueue.filter(
      (j): j is AlbumJob =>
        j.jobType === 'album' &&
        j.conceptId === conceptId &&
        j.status === RenderStatus.COMPLETED &&
        j.albumPublicUrl !== null
    ),

  getAllAlbumsForConcept: (conceptId: string): AlbumJob[] =>
    get().jobQueue.filter(
      (j): j is AlbumJob => j.jobType === 'album' && j.conceptId === conceptId
    ),
});
